import { useCallback, useEffect, useState } from 'react';
import type { CSVFileSelectionState } from './siteImportModalTypes';

type CSVFileImportModalState = {
  activeStep: number;
  fileSelection: CSVFileSelectionState;
};

const INITIAL_FILE_SELECTION: CSVFileSelectionState = {
  error: null,
  fileData: [],
  fileLoading: false,
  fileName: '',
  fileType: null,
};

function createInitialState(): CSVFileImportModalState {
  return {
    activeStep: 1,
    fileSelection: INITIAL_FILE_SELECTION,
  };
}

export function useCSVFileImportModal(
  isOpen: boolean,
  handleClose: () => void
) {
  const [state, setState] = useState(createInitialState);

  const resetState = useCallback(() => {
    setState(createInitialState());
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen, resetState]);

  const handleNextStep = useCallback(() => {
    setState((previousState) => ({
      ...previousState,
      activeStep: 2,
    }));
  }, []);

  const handlePrevStep = useCallback(() => {
    setState((previousState) => ({
      ...previousState,
      activeStep: 1,
    }));
  }, []);

  const closeImportDialog = useCallback(() => {
    resetState();
    handleClose();
  }, [handleClose, resetState]);

  const updateFileSelection = useCallback(
    (updates: Partial<CSVFileSelectionState>) => {
      setState((previousState) => ({
        ...previousState,
        fileSelection: {
          ...previousState.fileSelection,
          ...updates,
        },
      }));
    },
    []
  );

  return {
    ...state,
    handleNextStep,
    handlePrevStep,
    closeImportDialog,
    updateFileSelection,
  };
}
